
import React, { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Calendar } from 'lucide-react';
import { addDays, format, parse, subDays, subWeeks, isValid } from 'date-fns';

const PregnancyConceptionCalculator = () => {
  const [dueDate, setDueDate] = useState('');
  const [results, setResults] = useState<{
    conceptionDate?: string;
    lastPeriodDate?: string;
    fertilePeriodStart?: string;
    fertilePeriodEnd?: string;
    message?: string;
  }>({});

  const handleCalculate = () => {
    try {
      // Parse the due date from string to Date object
      const dueDateObj = parse(dueDate, 'yyyy-MM-dd', new Date());
      
      if (!isValid(dueDateObj)) {
        setResults({ message: 'Please enter a valid due date' });
        return;
      }
      
      // Calculate conception date (typically 266 days before due date)
      const conceptionDate = subDays(dueDateObj, 266);
      
      // Calculate last period date (typically 280 days before due date or 14 days before conception)
      const lastPeriodDate = subDays(dueDateObj, 280);
      
      // Calculate fertile window (typically 5 days before ovulation and day of ovulation)
      // Ovulation is approximately 14 days after the start of the last period
      const ovulationDate = addDays(lastPeriodDate, 14);
      const fertilePeriodStart = subDays(ovulationDate, 5);
      const fertilePeriodEnd = ovulationDate;

      setResults({
        conceptionDate: format(conceptionDate, 'MMMM d, yyyy'),
        lastPeriodDate: format(lastPeriodDate, 'MMMM d, yyyy'),
        fertilePeriodStart: format(fertilePeriodStart, 'MMMM d, yyyy'),
        fertilePeriodEnd: format(fertilePeriodEnd, 'MMMM d, yyyy')
      });
    } catch (error) {
      setResults({ message: 'Error calculating conception date. Please check your input.' });
    }
  };

  return (
    <Card className="w-full">
      <CardContent className="pt-6">
        <div className="space-y-6">
          <div className="space-y-4">
            <h3 className="text-lg font-medium">Pregnancy Conception Calculator</h3>
            <p className="text-sm text-muted-foreground">
              Calculate your estimated conception date based on your due date.
            </p>
          </div>
          
          <div className="grid gap-4">
            <div className="grid gap-2">
              <Label htmlFor="dueDate">Expected Due Date</Label>
              <div className="flex">
                <Input
                  id="dueDate"
                  type="date"
                  value={dueDate}
                  onChange={(e) => setDueDate(e.target.value)}
                  className="flex-1"
                />
                <div className="ml-2 bg-muted w-10 flex items-center justify-center rounded-md">
                  <Calendar className="h-4 w-4" />
                </div>
              </div>
              <p className="text-xs text-muted-foreground">
                Enter your expected due date to calculate the estimated conception date.
              </p>
            </div>
            
            <Button onClick={handleCalculate} className="mt-2">
              Calculate
            </Button>
          </div>

          {results.message ? (
            <div className="mt-4 p-4 bg-red-50 text-red-700 rounded-md">
              {results.message}
            </div>
          ) : results.conceptionDate ? (
            <div className="mt-6 space-y-4">
              <div className="p-4 border rounded-md">
                <h4 className="text-lg font-medium mb-4">Conception Information</h4>
                <div className="space-y-3">
                  <div>
                    <p className="text-sm font-medium">Estimated Conception Date:</p>
                    <p className="text-lg font-semibold text-primary">{results.conceptionDate}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium">First Day of Last Period:</p>
                    <p>{results.lastPeriodDate}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium">Fertile Window:</p>
                    <p>{results.fertilePeriodStart} to {results.fertilePeriodEnd}</p>
                  </div>
                </div>
              </div>
              
              <div className="text-sm text-muted-foreground">
                <p>Note: The conception date is typically around 266 days before your due date. Conception usually occurs during your fertile window, which is the 5-6 days leading up to and including ovulation.</p>
              </div>
            </div>
          ) : null}
        </div>
      </CardContent>
    </Card>
  );
};

export default PregnancyConceptionCalculator;
